import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { computePartnerMetrics } from "./partnerMetrics";
import { projectMetricsInsight, type MetricsInsightProjection } from "./metricsInsight";
import { metricsPortfolioQueryOptions } from "./metrics.queries";
import {
  DEFAULT_FILTERS,
  DEFAULT_SORT,
  applyFilters,
  isDefaultFilters,
  nextSort,
  sortRows,
} from "./metricsView";
import type { Tier } from "./model";
import type { MetricsSort, MetricsSortColumn } from "./metricsColumns";
import type { MetricsFilters, PartnerMetricsRow } from "./metrics.types";

type NonPartnerFilter = Exclude<keyof MetricsFilters, "partnerId">;

export interface MetricsDashboardModel {
  partners: {
    /** Filtered + sorted rows for the desktop and mobile adapters. */
    rows: PartnerMetricsRow[];
    /** Total Partners before filtering. */
    totalCount: number;
    sort: MetricsSort;
    toggleSort: (column: MetricsSortColumn) => void;
  };
  filters: {
    value: MetricsFilters;
    /** Every Partner, regardless of the active filters. */
    partnerOptions: PartnerMetricsRow[];
    tiers: Tier[];
    isDefault: boolean;
    set: <K extends NonPartnerFilter>(key: K, value: MetricsFilters[K]) => void;
    reset: () => void;
  };
  selection: {
    /** Resolved from the unfiltered portfolio, never from the displayed rows. */
    partner: PartnerMetricsRow | undefined;
    selectPartner: (id: string | "all") => void;
  };
  insight: MetricsInsightProjection;
}

export type UseMetricsDashboardResult =
  | { status: "pending" }
  | { status: "error"; error: Error }
  | { status: "success"; dashboard: MetricsDashboardModel };

/**
 * The Metrics Dashboard's single stateful hook: fetch the portfolio, compute
 * every partner row once, then hold the filter/sort UI state and re-derive the
 * visible rows through `metricsView` and the display-ready insight through
 * `metricsInsight`. The page component stays declarative.
 */
export function useMetricsDashboard(): UseMetricsDashboardResult {
  const { data, status, error } = useQuery(metricsPortfolioQueryOptions());
  const [filters, setFilters] = useState<MetricsFilters>(DEFAULT_FILTERS);
  const [sort, setSort] = useState<MetricsSort>(DEFAULT_SORT);

  const allRows = useMemo(() => (data ? computePartnerMetrics(data) : []), [data]);

  const rows = useMemo(
    () => sortRows(applyFilters(allRows, filters), sort),
    [allRows, filters, sort]
  );

  const insight = useMemo(() => projectMetricsInsight({ kind: "portfolio", rows }), [rows]);

  const selectedPartner = useMemo(
    () =>
      filters.partnerId === "all" ? undefined : allRows.find((row) => row.id === filters.partnerId),
    [allRows, filters.partnerId]
  );

  const setFilter = useCallback(<K extends NonPartnerFilter>(key: K, value: MetricsFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const selectPartner = useCallback((id: string | "all") => {
    setFilters((prev) => ({ ...prev, partnerId: id }));
  }, []);

  const resetFilters = useCallback(() => setFilters(DEFAULT_FILTERS), []);

  const toggleSort = useCallback(
    (column: MetricsSortColumn) => setSort((prev) => nextSort(prev, column)),
    []
  );

  if (status === "pending") return { status };
  if (status === "error") return { status, error: error as Error };

  return {
    status,
    dashboard: {
      partners: { rows, totalCount: allRows.length, sort, toggleSort },
      filters: {
        value: filters,
        partnerOptions: allRows,
        tiers: data.tiers,
        isDefault: isDefaultFilters(filters),
        set: setFilter,
        reset: resetFilters,
      },
      selection: { partner: selectedPartner, selectPartner },
      insight,
    },
  };
}
